import { animated, useTransition } from "@react-spring/web"
import { useLocation } from "@tanstack/react-router"
import { type ReactNode } from "react"

interface ViewTransitionProps {
  children: ReactNode
  className?: string
  transition: "fade" | "push" | "none"
}

// Detect React Native WebView context — animation can get stuck at opacity: 0
// on first mount in WKWebView, causing invisible but selectable content.
const isReactNativeWebView = typeof window !== "undefined" && !!(window as any).ReactNativeWebView

export const ViewTransition = ({
  children,
  className = "",
  transition = "fade"
}: ViewTransitionProps) => {
  const location = useLocation()

  const transitions = useTransition(location.pathname, {
    keys: location.pathname,
    initial: transition === "fade" ? { opacity: 1 } : { opacity: 1, x: 0 },
    from: transition === "fade" ? { opacity: 0 } : { opacity: 0, x: 100 },
    enter: transition === "fade" ? { opacity: 1 } : { opacity: 1, x: 0 },
    leave: transition === "fade" ? { opacity: 0 } : { opacity: 0, x: -100 },
    config: transition === "push" ? { tension: 500, friction: 50 } : { duration: 300 },
    exitBeforeEnter: true
  })

  // Skip react-spring animation in React Native WebView to avoid
  // first-render blank screen caused by stuck spring animation
  if (transition === "none" || isReactNativeWebView) {
    return <>{children}</>
  }

  return (
    <>
      {transitions((style, item) => (
        <animated.div
          key={item}
          className={className}
          style={
            transition === "push"
              ? {
                  ...style,
                  position: "absolute",
                  width: "100%",
                  height: "100%"
                }
              : style
          }
        >
          {children}
        </animated.div>
      ))}
    </>
  )
}
